import React, {useState} from 'react';
import {Avatar, Button, Card, Tag, Tooltip, Typography} from 'antd';
import {DownOutlined, RobotOutlined, ToolOutlined, UpOutlined, UserOutlined} from '@ant-design/icons';
import {ChatMessage as ChatMessageType} from '../../types/chat';
import MarkdownRenderer from '../common/MarkdownRenderer';
import dayjs from 'dayjs';

const {Text} = Typography;

// 格式化时间
const formatDateTime = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
    }
    if (value instanceof Date) {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
    }
    return String(value);
};

// 工具名称映射为中文
const toolNameMap: Record<string, string> = {
    'get_kline': 'K线查询',
    'get_position': '持仓查询',
    'get_balance': '余额查询',
    'trade': '交易执行',
    'cancel_order': '撤销订单',
    'set_stop_order': '设置止盈止损',
    'get_candle_patterns': 'K线形态',
    'get_zone_patterns': '区域形态',
    'get_radar': '多空雷达',
    'submit_final_output': '最终决策',
};

const getToolDisplayName = (functionName?: string): string => {
    if (!functionName) return '工具调用';
    return toolNameMap[functionName] || functionName;
};

/**
 * 原始 FC 参数结构（与后端 output 函数参数对齐）
 */
interface FcRawInfo {
    scene?: string;
    reasoning?: string;
    analysis?: string;
    references?: string[];
    data?: any;
}

/**
 * 解析消息的 rawJson（FC 原始参数）
 */
const parseFcRaw = (rawJson?: string): FcRawInfo | null => {
    if (!rawJson || !rawJson.trim()) return null;
    try {
        const obj = JSON.parse(rawJson);
        if (!obj || typeof obj !== 'object') return null;
        return obj as FcRawInfo;
    } catch (e) {
        return null;
    }
};

/**
 * 安全字符串化（AGENT_OUTPUT data 展示用）
 */
const safeJson = (value: any): string => {
    try {
        return JSON.stringify(value, null, 2);
    } catch (e) {
        return String(value);
    }
};

/**
 * 移除 markdown ```json 代码块（AGENT_OUTPUT 卡片已展示结构化数据时避免重复）
 */
const stripJsonCodeBlocks = (content: string): string =>
    content.replace(/```json\s*\n?([\s\S]*?)\n?```/gi, '').trim();

/**
 * 预处理内容函数：将 <thinking> 标签转换为 markdown 引用块格式，处理纯JSON
 * 注意：此函数与 MarkdownRenderer.tsx 中的 preprocessContent 保持一致
 */
const preprocessContent = (content: string): string => {
    // 1. 先提取thinking内容并转换为markdown引用块格式
    let thinkingMarkdown = '';
    const resultWithThinking = content.replace(/<thinking>([\s\S]*?)<\/thinking>/gi, (match, thinkingContent) => {
        const quotedContent = thinkingContent.trim()
            .split('\n')
            .map((line: string) => `> ${line}`)
            .join('\n');
        thinkingMarkdown = `**🤔 思考过程:**\n${quotedContent}`;
        return ''; // 移除thinking标签
    });

    // 2. 检测剩余内容是否为JSON
    let result = resultWithThinking.trim();
    if (!result.includes('```json') && !result.includes('```JSON')) {
        if (result.startsWith('{') || result.startsWith('[')) {
            result = '```json\n' + result + '\n```';
        }
    }

    // 3. 组合thinking和JSON内容
    if (thinkingMarkdown) {
        return thinkingMarkdown + '\n\n' + result;
    }
    return result;
};

interface ChatMessageProps {
    message: ChatMessageType;
}

/**
 * 折叠阈值配置
 */
const COLLAPSE_THRESHOLD = 150; // 字符数超过150时自动折叠

const ChatMessage: React.FC<ChatMessageProps> = React.memo(({message}) => {
    const isUser = message.role === 'user';
    const isTool = message.role === 'tool';

    // FC 原始参数解析（rawJson）
    const fcRaw = parseFcRaw(message.rawJson);
    const showFcBlock = !isUser && !isTool && fcRaw !== null;
    const isAgentOutputCard = showFcBlock && fcRaw?.scene === 'AGENT_OUTPUT';

    // 折叠状态
    const [isExpanded, setIsExpanded] = useState(false);
    // tool 角色默认折叠，点击后展开
    const [isToolExpanded, setIsToolExpanded] = useState(false);

    const hiddenKinds = ['STARTING_GUN_CALL', 'STARTING_GUN_RESULT', 'OUTPUT_REPAIR_FEEDBACK', 'SYSTEM_PROMPT'];
    if (message.messageKind && hiddenKinds.includes(message.messageKind)) {
        return null;
    }
    // TOOL_CALL / TOOL_RESULT 在展开模式下显示，否则隐藏
    if (message.messageKind === 'TOOL_CALL' || message.messageKind === 'TOOL_RESULT') {
        if (!isToolExpanded) return null;
    }

    // 向后兼容：无 messageKind 时使用内容启发式过滤
    const isSystemCall = message.content?.startsWith('[function_call]')
        || message.content?.startsWith('[starting_gun]')
        || message.content?.startsWith('[output_validation]')
        || message.content?.startsWith('[output_validation_aborted]')
        || message.functionName === 'output_validation'
        || message.functionName === 'output_repair_feedback'
        || message.functionName === 'submit_final_output'
        || message.functionName === '__suspended__';
    if (isSystemCall) return null;

    // 判断是否需要显示折叠按钮
    const shouldShowCollapseButton = () => {
        let content = message.content;

        // 对用户消息进行标题格式转换：=== {title} === => ## {title}
        if (isUser) {
            content = content.replace(/===\s*([^=]+)\s*===/g, '## $1');
        }

        return content.length > COLLAPSE_THRESHOLD;
    };

    // 获取完整的内容（用于复制）
    const getFullContent = () => {
        let content = message.content;

        // 对用户消息进行标题格式转换：=== {title} === => ## {title}
        if (isUser) {
            content = content.replace(/===\s*([^=]+)\s*===/g, '## $1');
        }

        // 非用户消息，先进行预处理（处理thinking标签和JSON）
        if (!isUser) {
            content = preprocessContent(content);
        }
        // AGENT_OUTPUT 卡片已展示 data，正文隐藏重复的 JSON 代码块
        if (isAgentOutputCard) {
            content = stripJsonCodeBlocks(content);
        }

        return content;
    };

    // 获取显示的内容
    const getDisplayContent = () => {
        let content = message.content;

        // 对用户消息进行标题格式转换：=== {title} === => ## {title}
        if (isUser) {
            content = content.replace(/===\s*([^=]+)\s*===/g, '## $1');
        }

        // 非用户消息，先进行预处理（处理thinking标签和JSON）
        if (!isUser) {
            content = preprocessContent(content);
        }
        // AGENT_OUTPUT 卡片已展示 data，正文隐藏重复的 JSON 代码块
        if (isAgentOutputCard) {
            content = stripJsonCodeBlocks(content);
        }

        // 判断是否需要折叠（基于转换后的内容）
        const shouldCollapseContent = content.length > COLLAPSE_THRESHOLD;

        if (!shouldCollapseContent || isExpanded) {
            return content;
        }
        return content.substring(0, COLLAPSE_THRESHOLD) + '...';
    };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                marginBottom: 16,
                padding: '0 8px'
            }}
        >
            <div
                style={{
                    maxWidth: '80%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 8
                }}
            >
                {/* 头像 */}
                <Avatar
                    size="small"
                    icon={isUser ? <UserOutlined/> : isTool ? <ToolOutlined/> : <RobotOutlined/>}
                    style={{
                        backgroundColor: isUser ? 'var(--pt-primary)' : isTool ? 'var(--pt-amber)' : 'var(--pt-bull)',
                        flexShrink: 0,
                        cursor: isTool ? 'pointer' : 'default',
                        transition: isTool ? 'all 0.2s' : undefined,
                        boxShadow: isTool ? (isToolExpanded ? '0 0 8px var(--pt-amber)' : 'none') : undefined
                    }}
                    onClick={() => isTool && setIsToolExpanded(!isToolExpanded)}
                />

                {/* 消息内容 - tool 角色默认折叠，点击头像后展开 */}
                {(!isTool || isToolExpanded) && (
                    <Card
                        size="small"
                        style={{
                            backgroundColor: isUser ? 'rgba(24, 144, 255, 0.3)' : isTool ? '#3a3a3a' : '#2a2a2a',
                            border: isUser ? 'none' : '1px solid #404040',
                            borderRadius: 18,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            position: 'relative',
                            width: 'fit-content',
                            maxWidth: '100%',
                            minWidth: '200px'
                        }}
                        styles={{
                            body: {
                                padding: '12px 16px',
                                maxWidth: '100%'
                            }
                        }}
                    >
                        {/* tool 消息显示工具名称标签 */}
                        {isTool && message.functionName && (
                            <div style={{marginBottom: 8}}>
                                <Tag color="orange" style={{fontSize: 11, lineHeight: '18px', padding: '0 6px'}}>
                                    <ToolOutlined style={{marginRight: 4}}/>
                                    {getToolDisplayName(message.functionName)}
                                </Tag>
                            </div>
                        )}
                        {/* FC 结构化展示：rawJson（scene=CHAT 思考过程/引用；scene=AGENT_OUTPUT 决策摘要） */}
                        {showFcBlock && fcRaw && (
                            <div style={{marginBottom: 10}}>
                                {fcRaw.scene === 'CHAT' && fcRaw.analysis && (
                                    <div
                                        style={{
                                            borderLeft: '3px solid #F0B429',
                                            backgroundColor: 'rgba(240, 180, 41, 0.08)',
                                            padding: '6px 10px',
                                            borderRadius: 4,
                                            marginBottom: 6
                                        }}
                                    >
                                        <div style={{fontSize: 11, color: 'var(--pt-primary)', marginBottom: 4}}>🤔
                                            思考过程
                                        </div>
                                        <div style={{
                                            color: '#d9d9d9',
                                            fontSize: 13,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}>
                                            {fcRaw.analysis}
                                        </div>
                                    </div>
                                )}
                                {fcRaw.scene === 'AGENT_OUTPUT' && (
                                    <div style={{marginBottom: 6}}>
                                        <Tag color="gold" style={{fontSize: 11, lineHeight: '18px'}}>📋 决策输出</Tag>
                                        <pre
                                            style={{
                                                maxHeight: 240,
                                                overflow: 'auto',
                                                margin: '6px 0 0',
                                                padding: 8,
                                                backgroundColor: 'rgba(0,0,0,0.25)',
                                                borderRadius: 6,
                                                fontSize: 12,
                                                color: '#bfbfbf',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {safeJson(fcRaw.data ?? {})}
                                        </pre>
                                    </div>
                                )}
                                {Array.isArray(fcRaw.references) && fcRaw.references.length > 0 && (
                                    <div style={{marginTop: 4}}>
                                        {fcRaw.references.map((ref, i) => (
                                            <Tag
                                                key={i}
                                                color="blue"
                                                style={{fontSize: 11, lineHeight: '18px', marginBottom: 2}}
                                            >
                                                {ref}
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* 消息文本 - 用户和系统响应都使用Markdown渲染 */}
                        <div
                            style={{
                                color: '#ffffff',
                                maxWidth: '100%',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                display: 'block',
                                minWidth: '200px'
                            }}
                        >
                            <MarkdownRenderer content={getDisplayContent()} isUser={isUser}
                                              fullContent={getFullContent()}/>
                        </div>

                        {/* 展开/折叠按钮 - 右上角绝对定位，在复制按钮右侧 */}
                        {shouldShowCollapseButton() && (
                            <Tooltip title={isExpanded ? '收起' : '展开全文'} placement="top">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={isExpanded ? <UpOutlined/> : <DownOutlined/>}
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    style={{
                                        position: 'absolute',
                                        top: '13px',
                                        right: '17px',
                                        backgroundColor: 'transparent',
                                        borderColor: 'transparent',
                                        color: isExpanded ? 'var(--pt-primary)' : '#ffffff',
                                        zIndex: 10,
                                        opacity: 0.9
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = '0.9';
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* tool 角色折叠按钮 */}
                        {isTool && isToolExpanded && (
                            <Tooltip title='收起工具结果' placement="top">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<UpOutlined/>}
                                    onClick={() => setIsToolExpanded(false)}
                                    style={{
                                        position: 'absolute',
                                        top: '13px',
                                        right: '17px',
                                        backgroundColor: 'transparent',
                                        borderColor: 'transparent',
                                        color: 'var(--pt-amber)',
                                        zIndex: 10,
                                        opacity: 0.9
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = '0.9';
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* 时间戳 */}
                        <div style={{marginTop: 4}}>
                            <Text
                                style={{fontSize: 11, color: 'rgba(255, 255, 255, 0.45)'}}
                            >
                                {formatDateTime(message.createdTime)}
                            </Text>
                        </div>
                    </Card>
                )}
                {/* tool 角色提示文字 */}
                {isTool && !isToolExpanded && (
                    <Text style={{fontSize: 12, color: 'rgba(255, 255, 255, 0.45)', marginLeft: 8}}>
                        点击图标查看工具调用结果
                    </Text>
                )}
            </div>
        </div>
    );
});

export default ChatMessage;
